import React, { useState, useContext, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import { certificates } from '../data/certificates';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SectionHeader from '../components/styled/SectionHeader';
import { 
    faXmark, 
    faArrowUpRightFromSquare,
    faAward 
} from '@fortawesome/free-solid-svg-icons';

// Grid stagger variants
const gridVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.12, delayChildren: 0.1 }
    }
};

// Card entry variants
const cardVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 120, damping: 16 }
    }
};

const Certificates = () => {
    const { theme, themes } = useContext(ThemeContext);
    const [selectedCertificate, setSelectedCertificate] = useState(null);
    const isLight = themes[theme]?.isIconDark;

    // Close on Escape key press
    useEffect(() => {
        if (!selectedCertificate) return undefined;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setSelectedCertificate(null);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedCertificate]);

    return (
        <div className="w-full max-w-6xl mx-auto px-4 pt-2 pb-8">
            <SectionHeader
                badgeLabel="Accredited"
                badgeCount={certificates.length}
                badgeUnit="Verified"
                badgeIcon={faAward}
                title="Certifications"
                subtitle="Verified credentials in iOS architecture, Swift, and systems design"
            />

            {/* Certificates Grid */}
            <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                initial="hidden"
                animate="visible"
                variants={gridVariants}
            >
                {certificates.map((cert) => (
                    <motion.div
                        role="button"
                        tabIndex={0}
                        aria-label={`View certificate: ${cert.title}`}
                        aria-haspopup="dialog"
                        key={cert.id}
                        variants={cardVariants}
                        whileHover={{ scale: 1.02, y: -3 }}
                        transition={{ type: "spring", stiffness: 500, damping: 28 }}
                        onClick={() => setSelectedCertificate(cert)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setSelectedCertificate(cert);
                            }
                        }}
                        className={`group relative flex w-full flex-col justify-between rounded-2xl overflow-hidden border text-left shadow-lg hover:shadow-xl cursor-pointer transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${
                            isLight
                                ? 'bg-black/[0.03] border-black/10 hover:border-black/25 hover:bg-black/[0.05]'
                                : 'bg-white/[0.03] border-white/10 hover:border-white/30 hover:bg-white/[0.06]'
                        }`}
                    >
                        {/* Certificate Image Frame */}
                        <div className={`relative aspect-[16/11] w-full overflow-hidden flex items-center justify-center p-3 border-b ${
                            isLight ? 'bg-black/5 border-black/5' : 'bg-black/40 border-white/5'
                        }`}>
                            <img
                                src={cert.image}
                                alt={cert.title}
                                className="w-full h-full object-contain rounded-lg"
                                loading="lazy"
                            />
                        </div>

                        {/* Certificate Details */}
                        <div className="p-5 flex flex-col justify-start">
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <h3 className={`text-base font-bold tracking-tight truncate ${themes[theme].text}`}>
                                        {cert.title}
                                    </h3>
                                    {/* Verified Tick */}
                                    <svg
                                        role="img"
                                        className="w-4 h-4 fill-emerald-500 drop-shadow-[0_0_4px_rgba(16,185,129,0.4)] shrink-0 translate-y-[0.5px]" 
                                        viewBox="0 0 24 24"
                                        aria-label="Verified"
                                    >
                                        <path d="M12 1L9.27 3.42L5.7 3.25L4.41 6.64L1.13 8.23L1.92 11.77L0.16 15L2.61 17.65L2.83 21.23L6.34 22L8.53 24.84L12 23.63L15.47 24.84L17.66 22L21.17 21.23L21.39 17.65L23.84 15L22.08 11.77L22.87 8.23L19.59 6.64L18.3 3.25L14.73 3.42L12 1Z" />
                                        <path 
                                            d="M10.2 16.2L6.3 12.3L7.7 10.9L10.2 13.4L16.3 7.3L17.7 8.7L10.2 16.2Z" 
                                            fill="#ffffff" 
                                        />
                                    </svg>
                                </div>
                            </div>

                            <p className={`text-xs mt-2 leading-relaxed line-clamp-2 ${themes[theme].subText}`}>
                                {cert.description}
                            </p>
                        </div>
                    </motion.div>
                ))}
            </motion.div>

            {/* Modal for Enlarged Certificate */}
            <AnimatePresence>
                {selectedCertificate && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 md:p-10 bg-black/85 backdrop-blur-md cursor-zoom-out"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedCertificate(null)}
                    >
                        {/* Close button */}
                        <button
                            type="button"
                            onClick={() => setSelectedCertificate(null)}
                            className="absolute top-5 right-5 text-white/70 hover:text-white p-2 text-xl z-20 rounded-full hover:bg-white/10 transition-colors"
                            aria-label="Close certificate modal"
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>

                        {/* Modal Box */}
                        <motion.div
                            className="relative max-w-4xl max-h-[85vh] flex flex-col items-center bg-black/70 rounded-2xl border border-white/15 overflow-hidden shadow-2xl p-2 sm:p-4 cursor-default"
                            initial={{ scale: 0.9, opacity: 0, y: 10 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 10 }}
                            transition={{ type: "spring", damping: 25, stiffness: 280 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="relative w-full overflow-hidden flex items-center justify-center max-h-[70vh]">
                                <img
                                    src={selectedCertificate.image}
                                    alt={selectedCertificate.title}
                                    className="w-full h-full object-contain rounded-lg shadow-md"
                                />
                            </div>

                            {/* Modal Info Footer */}
                            <div className="w-full pt-3 px-2 flex items-center justify-between">
                                <div className="text-left">
                                    <h4 className="text-white text-sm font-semibold">
                                        {selectedCertificate.title}
                                    </h4>
                                    <p className="text-white/60 text-xs mt-0.5 line-clamp-1">
                                        {selectedCertificate.description}
                                    </p>
                                </div>

                                {selectedCertificate.verifyUrl && (
                                    <a
                                        href={selectedCertificate.verifyUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium transition-colors ml-4 whitespace-nowrap"
                                    >
                                        <span>Verify Credential</span>
                                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
                                    </a>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Certificates;
